import React from 'react';
import { getInitials } from '../lib/utils';

const ResultsGrid = ({
  isSignedIn,
  viewMode,
  results,
  candidateSaved,
  candidateEmails,
  emailLoading,
  onGetEmail,
  onMoreInfo,
  hasMoreCandidates,
  onLoadMore,
  loadMoreLoading,
  searchLoading,
}) => {
  if (!isSignedIn || viewMode !== 'cards') return null;

  const stripLocationFromCompany = company => {
    if (!company) return '';
    const withoutParens = company.replace(/\s*\(.*?\)/g, '').trim();
    const withoutDash = withoutParens.split(' - ')[0].trim();
    return withoutDash || withoutParens;
  };

  const cards = [];
  results.forEach((result, idx) => {
    if (result.type === 'assistant' && Array.isArray(result.content)) {
      result.content.forEach((item, itemIdx) => {
        if (item.type === 'career_candidate') {
          cards.push({ key: `${idx}-${itemIdx}`, item });
        }
      });
    }
  });

  if (cards.length === 0 && !searchLoading && !loadMoreLoading) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {cards.map(({ key, item }) => (
          <div
            key={key}
            className="bg-[#242831] rounded-lg p-5 shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all border border-[#374151]"
          >
            <div className="flex gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center text-white text-lg font-semibold flex-shrink-0">
                {getInitials(item.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#e4e6eb] text-base mb-1 truncate">{item.name}</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {item.current_position && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#374151] rounded text-xs text-[#e4e6eb] shadow-sm">
                      💼 {item.current_position}
                    </span>
                  )}
                  {stripLocationFromCompany(item.company) && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#2f3643] rounded text-xs text-[#e4e6eb] shadow-sm">
                      🏢 {stripLocationFromCompany(item.company)}
                    </span>
                  )}
                  {item.location && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#374151] rounded text-xs text-[#9ca3af]">
                      📍 {item.location}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {item.linkedin_url && (
              <a
                href={item.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#10b981] text-xs hover:underline mb-3 block"
              >
                View LinkedIn →
              </a>
            )}
            <div className="flex gap-2 pt-4 border-t border-[#374151]">
              <button
                onClick={() => onGetEmail(item)}
                disabled={emailLoading[item.id] || candidateSaved[item.id]}
                className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  candidateSaved[item.id] ? 'bg-[#059669] text-white cursor-default' : 'bg-[#10b981] text-white hover:bg-[#059669]'
                } ${emailLoading[item.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {emailLoading[item.id] ? 'Getting...' : candidateSaved[item.id] ? '✓ Saved' : '💾 Save'}
              </button>
              <button
                onClick={() => onMoreInfo(item)}
                className="flex-1 px-3 py-2 bg-[#374151] text-[#e4e6eb] rounded-lg text-xs font-medium hover:bg-[#4b5563] transition-colors"
              >
                ℹ️ More Info
              </button>
            </div>
            {candidateSaved[item.id] && candidateEmails[item.id] && (
              <div className="mt-2 text-xs text-[#9ca3af]">
                Email: <span className="font-mono text-[#e4e6eb]">{candidateEmails[item.id]}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {hasMoreCandidates && (
        <div className="flex justify-center mt-6 mb-6">
          <button
            onClick={onLoadMore}
            className="px-6 py-3 rounded-lg bg-[#10b981] text-white font-semibold hover:bg-[#059669] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loadMoreLoading}
          >
            {loadMoreLoading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {(searchLoading || loadMoreLoading) && (
        <div className="flex justify-center py-8">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-[#10b981] rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-[#10b981] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-[#10b981] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      )}
    </>
  );
};

export { ResultsGrid };
